import React, { useState } from "react";
import {
    commonService,
    AppConfigData,
} from "../_services/common.service";
import _Skeleton, { _SkeletonType } from "./_Skeleton";
import _NoDataFound from "./_NoDataFound";
import { useHistory, Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import { Map, GoogleApiWrapper, Polyline, Marker } from "google-maps-react";
import * as signalR from "@microsoft/signalr";
import ToDoTask from "./ToDoTask";
import Wall from "./Wall";
import BasicInformationView from "./BasicInformationView";
import ParentView from "./ParentView";
import FeeView from "./FeeView";
import ExamView from "./ExamView";
import SiblingView from "./SiblingView";
import TransportationView from "./TransportationView";
import Healthview from "./HealthView";
import OtherInfoView from "./OtherInfoView";
import ChildCustodyView from "./ChildCustodyview";
import RewardView from "./RewardView";
import SubjectEnrollmentView from "./SubjectEnrollmentView";
import HostelDetailView from "./HostelView";
import DocumentView from "./DocumentView";
import schoolBusIconTop from "../Images/Icon/school-bus-top.png";

let preloadedTopBus = null;
const rotatedBusCache = {};

if (typeof window !== "undefined") {
    preloadedTopBus = new Image();
    preloadedTopBus.src = schoolBusIconTop;
}

const getRotatedBusIconUrl = (angle, targetW, targetH) => {
    if (typeof document === "undefined") {
        return schoolBusIconTop;
    }

    if (!preloadedTopBus) {
        preloadedTopBus = new Image();
        preloadedTopBus.src = schoolBusIconTop;
    }

    if (!preloadedTopBus.complete || preloadedTopBus.naturalWidth === 0) {
        return schoolBusIconTop;
    }

    const normAngle = ((Math.round((angle || 0) / 2) * 2) % 360 + 360) % 360;
    const cacheKey = `${normAngle}_${targetW}_${targetH}`;
    if (rotatedBusCache[cacheKey]) {
        return rotatedBusCache[cacheKey];
    }

    try {
        const diag = Math.ceil(Math.sqrt(targetW * targetW + targetH * targetH));
        const scale = 2; // Crisp retina rendering
        const canvas = document.createElement("canvas");
        canvas.width = diag * scale;
        canvas.height = diag * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return schoolBusIconTop;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.translate((diag * scale) / 2, (diag * scale) / 2);
        ctx.rotate((normAngle * Math.PI) / 180);
        ctx.drawImage(preloadedTopBus, (-targetW * scale) / 2, (-targetH * scale) / 2, targetW * scale, targetH * scale);

        const dataUrl = canvas.toDataURL("image/png");
        rotatedBusCache[cacheKey] = dataUrl;
        return dataUrl;
    } catch (e) {
        return schoolBusIconTop;
    }
};

const MapStyle = {
    width: "100%",
    height: "100%",
    position: "relative",
};

// Helper to convert NMEA format to Decimal Degrees for Google Maps
const parseNMEACoordinate = (nmeaStr) => {
    if (!nmeaStr) return 0;
    const str = String(nmeaStr);
    const decimalIdx = str.indexOf(".");
    if (decimalIdx === -1 || decimalIdx <= 2) return parseFloat(str);
    const minutesStart = decimalIdx - 2;
    if (minutesStart <= 0) return parseFloat(str);
    const degrees = parseFloat(str.substring(0, minutesStart));
    const minutes = parseFloat(str.substring(minutesStart));
    return degrees + (minutes / 60);
};

// Helper to determine SignalR Hub URL dynamically for both localhost and live production
const getSignalRHubUrl = () => {
    //return "http://localhost:51919/gpsHub";
     return  AppConfigData.APIBasePath.replace("api/classroom/", "gpsHub");
};

// Calculates tracking window (-15 mins before start to +10 mins after end)
const getTrackingWindow = (startTimeStr, endTimeStr) => {
    if (!startTimeStr || !endTimeStr) return null;
    try {
        const parseTime = (tStr) => {
            if (!tStr) return null;
            let str = String(tStr).trim();
            if (str.includes("T")) {
                const dt = new Date(str);
                if (!isNaN(dt.getTime())) return dt;
            }
            let parts = str.split(' ');
            let time = parts[0];
            let modifier = parts.length > 1 ? parts[1] : null;
            let timePieces = time.split(':');
            if (timePieces.length < 2) return null;
            let h = parseInt(timePieces[0], 10);
            let m = parseInt(timePieces[1], 10);
            if (isNaN(h) || isNaN(m)) return null;

            if (modifier) {
                const mod = modifier.toUpperCase();
                if (mod === 'PM' && h < 12) h += 12;
                if (mod === 'AM' && h === 12) h = 0;
            }
            const d = new Date();
            d.setHours(h, m, 0, 0);
            return d;
        };

        let start = parseTime(startTimeStr);
        let end = parseTime(endTimeStr);
        if (!start || !end) return null;

        if (end.getTime() < start.getTime()) {
            end.setDate(end.getDate() + 1);
        }

        const winStart = new Date(start.getTime() - 15 * 60 * 1000);
        const winEnd = new Date(end.getTime() + 10 * 60 * 1000);
        return { start: winStart, end: winEnd };
    } catch (e) {
        return null;
    }
};

const isWithinTrackingTime = (startTimeStr, endTimeStr) => {
    const win = getTrackingWindow(startTimeStr, endTimeStr);
    if (!win) return false;
    const now = new Date();
    return now >= win.start && now <= win.end;
};

const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    const R = 6371e3;
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLon = (lon2 - lon1) * rad;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * rad) * Math.cos(lat2 * rad) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const getResponsiveBusIcon = (google, pos, path, width) => {
    const maps = google?.maps || window.google?.maps;
    const w = width || (typeof window !== "undefined" ? window.innerWidth : 1024);
    let iconHeight = 54;
    if (w < 480) {
        iconHeight = 40;
    } else if (w < 768) {
        iconHeight = 46;
    } else if (w < 1200) {
        iconHeight = 50;
    } else {
        iconHeight = 56;
    }

    // Top-down 3D bus image is 367 x 951 (ratio ~ 0.386)
    const iconWidth = Math.max(15, Math.round(iconHeight * (367 / 951)));

    let courseAngle = 0;
    if (pos && typeof pos.course !== "undefined" && pos.course !== null && !isNaN(parseFloat(pos.course))) {
        courseAngle = parseFloat(pos.course);
    } else if (path && path.length >= 2) {
        const last = path[path.length - 1];
        const prev = path[path.length - 2];
        const dLng = last.lng - prev.lng;
        const dLat = last.lat - prev.lat;
        courseAngle = (Math.atan2(dLng, dLat) * 180 / Math.PI + 360) % 360;
    }

    const iconUrl = getRotatedBusIconUrl(courseAngle, iconWidth, iconHeight);
    if (!maps) {
        return { url: iconUrl };
    }

    if (iconUrl === schoolBusIconTop) {
        return {
            url: schoolBusIconTop,
            scaledSize: new maps.Size(iconWidth, iconHeight),
            anchor: new maps.Point(Math.round(iconWidth / 2), Math.round(iconHeight / 2)),
        };
    }

    const diag = Math.ceil(Math.sqrt(iconWidth * iconWidth + iconHeight * iconHeight));
    return {
        url: iconUrl,
        scaledSize: new maps.Size(diag, diag),
        anchor: new maps.Point(Math.round(diag / 2), Math.round(diag / 2)),
    };
};

/**
 * SmoothLiveBusMarker:
 * Smoothly drives the bus marker along the road at 60 FPS
 * between real-time GPS packets (e.g. 3-second fetch interval).
 * Interpolates both position (lat/lng) and heading (course angle taking shortest turn).
 * Direct manipulation of google.maps.Marker gives native 60fps performance without React DOM churn.
 */
const SmoothLiveBusMarker = React.memo(function SmoothLiveBusMarker({
    map,
    google,
    targetPosition,
    liveBusPath,
    windowWidth,
    onPositionUpdate,
}) {
    const markerRef = React.useRef(null);
    const currentPosRef = React.useRef(null);
    const startPosRef = React.useRef(null);
    const targetPosRef = React.useRef(null);
    const startTimeRef = React.useRef(0);
    const durationRef = React.useRef(3000);
    const animFrameRef = React.useRef(null);
    const lastIconAngleRef = React.useRef(null);
    const lastPacketTimeRef = React.useRef(0);
    const lastThrottledUpdateRef = React.useRef(0);

    // Clean up marker on unmount
    React.useEffect(() => {
        return () => {
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = null;
            }
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }
        };
    }, []);

    // Handle marker creation and target updates
    React.useEffect(() => {
        if (!map || !google || !targetPosition || isNaN(targetPosition.lat) || isNaN(targetPosition.lng)) {
            return;
        }

        const maps = google.maps || window.google?.maps;
        if (!maps) return;

        const now = performance.now();

        // 1. Initial creation when first packet arrives
        if (!markerRef.current) {
            const initialIcon = getResponsiveBusIcon(google, targetPosition, liveBusPath, windowWidth);
            markerRef.current = new maps.Marker({
                map: map,
                position: new maps.LatLng(targetPosition.lat, targetPosition.lng),
                icon: initialIcon,
                title: "Live Bus Location",
                zIndex: 9999,
                optimized: false,
            });
            currentPosRef.current = { ...targetPosition };
            startPosRef.current = { ...targetPosition };
            targetPosRef.current = { ...targetPosition };
            lastIconAngleRef.current = targetPosition.course || 0;
            lastPacketTimeRef.current = now;
            if (onPositionUpdate) {
                onPositionUpdate(targetPosition, true);
            }
            return;
        }

        // 2. Measure interval between packets to dynamically adapt duration (clamped 1.5s - 5s)
        if (lastPacketTimeRef.current > 0) {
            const delta = now - lastPacketTimeRef.current;
            durationRef.current = Math.min(Math.max(delta, 1500), 5000);
        } else {
            durationRef.current = 3000;
        }
        lastPacketTimeRef.current = now;

        // Start position is current interpolated position of the vehicle
        const fromPos = currentPosRef.current || targetPosition;
        startPosRef.current = { ...fromPos };

        // Determine target heading/course
        let targetCourse = targetPosition.course;
        const dLng = targetPosition.lng - fromPos.lng;
        const dLat = targetPosition.lat - fromPos.lat;
        const distMoved = Math.hypot(dLat, dLng);

        // If distance moved is non-trivial and course is either absent or 0, compute forward angle
        if ((typeof targetCourse === "undefined" || targetCourse === null || targetCourse === 0) && distMoved > 0.00002) {
            targetCourse = (Math.atan2(dLng, dLat) * 180 / Math.PI + 360) % 360;
        } else if (typeof targetCourse === "undefined" || targetCourse === null) {
            targetCourse = fromPos.course || 0;
        }

        targetPosRef.current = {
            lat: targetPosition.lat,
            lng: targetPosition.lng,
            course: targetCourse,
        };

        startTimeRef.current = now;

        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }

        const animate = (currentTime) => {
            const elapsed = currentTime - startTimeRef.current;
            const fraction = Math.min(Math.max(elapsed / durationRef.current, 0), 1);

            // Linear position interpolation for real vehicle drive
            const curLat = startPosRef.current.lat + (targetPosRef.current.lat - startPosRef.current.lat) * fraction;
            const curLng = startPosRef.current.lng + (targetPosRef.current.lng - startPosRef.current.lng) * fraction;

            // Shortest angle rotation (-180 to +180 deg)
            const startAngle = startPosRef.current.course || 0;
            const destAngle = targetPosRef.current.course || 0;
            let diffAngle = ((destAngle - startAngle + 540) % 360) - 180;
            const curCourse = (startAngle + diffAngle * fraction + 360) % 360;

            const interpolatedPos = {
                lat: curLat,
                lng: curLng,
                course: curCourse,
            };
            currentPosRef.current = interpolatedPos;

            // Update marker directly on Google Map (60 FPS smooth!)
            if (markerRef.current) {
                markerRef.current.setPosition(new maps.LatLng(curLat, curLng));

                // Only rotate icon when angle changes by >= 2 degrees or on finish
                const angleDelta = Math.abs(((curCourse - (lastIconAngleRef.current || 0) + 540) % 360) - 180);
                if (angleDelta >= 2 || fraction === 1) {
                    const newIcon = getResponsiveBusIcon(google, interpolatedPos, liveBusPath, windowWidth);
                    markerRef.current.setIcon(newIcon);
                    lastIconAngleRef.current = curCourse;
                }
            }

            // Throttle parent polyline state update to ~200ms
            if (onPositionUpdate) {
                const timeSinceLast = currentTime - lastThrottledUpdateRef.current;
                if (timeSinceLast >= 200 || fraction === 1) {
                    lastThrottledUpdateRef.current = currentTime;
                    onPositionUpdate(interpolatedPos, fraction === 1);
                }
            }

            if (fraction < 1) {
                animFrameRef.current = requestAnimationFrame(animate);
            }
        };

        animFrameRef.current = requestAnimationFrame(animate);

    }, [map, google, targetPosition, windowWidth]);

    return null;
});

/**
 * Isolated Pickup Route Map Component.
 * ONLY connects to SignalR and updates when Pickup route time is active.
 * When it is Drop time, this component NEVER calls SignalR, receives no updates,
 * and renders as a completely normal static route without reloading or blinking.
 */
const PickupRouteMap = React.memo(function PickupRouteMap({
    google,
    transportationData,
    pickupMarker,
    initialCenter,
    windowWidth,
}) {
    const [targetBusPosition, setTargetBusPosition] = React.useState(null);
    const [liveBusPosition, setLiveBusPosition] = React.useState(null);
    const [liveBusPath, setLiveBusPath] = React.useState([]);
    const [hasArrived, setHasArrived] = React.useState(false);

    const handlePositionUpdate = React.useCallback((pos) => {
        setLiveBusPosition(pos);
    }, []);

    const stops = React.useMemo(() => {
        try {
            const raw = transportationData?.PickupRoutePoint;
            const list = raw ? JSON.parse(raw) : [];
            return list.map((point, index) => ({
                id: `pickup-stop-${index}`,
                position: {
                    lat: parseFloat(point.Latitude),
                    lng: parseFloat(point.Longitude)
                },
                label: (index + 1).toString(),
                title: point.PointName,
                raw: point
            }));
        } catch (e) {
            return [];
        }
    }, [transportationData?.PickupRoutePoint]);

    const isTrackingActive = React.useCallback(() => {
        if (!transportationData?.BusRouteForPickupStartTime || !transportationData?.BusRouteForPickupEndTime) return false;
        return isWithinTrackingTime(transportationData.BusRouteForPickupStartTime, transportationData.BusRouteForPickupEndTime);
    }, [transportationData?.BusRouteForPickupStartTime, transportationData?.BusRouteForPickupEndTime]);

    // ONLY initiates SignalR connection when Pickup tracking is active
    React.useEffect(() => {
        const deviceMac = transportationData?.GpsDeviceID;
        if (!deviceMac) {
            setTargetBusPosition(null);
            setLiveBusPosition(null);
            setLiveBusPath([]);
            return;
        }

        let connection = null;
        let scheduleTimer = null;
        let isCancelled = false;

        const stopTracking = () => {
            if (connection) {
                try { connection.stop(); } catch (e) { }
                connection = null;
            }
            setTargetBusPosition(null);
            setLiveBusPosition(null);
            setLiveBusPath([]);
        };

        const scheduleNextCheck = () => {
            if (scheduleTimer) {
                clearTimeout(scheduleTimer);
                scheduleTimer = null;
            }
            const now = new Date();
            const win = getTrackingWindow(transportationData?.BusRouteForPickupStartTime, transportationData?.BusRouteForPickupEndTime);
            if (!win) return;

            if (win.start > now) {
                const diff = win.start.getTime() - now.getTime();
                if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
                    scheduleTimer = setTimeout(() => {
                        syncTracking();
                        scheduleNextCheck();
                    }, diff);
                }
            } else if (now >= win.start && now <= win.end) {
                const diff = win.end.getTime() - now.getTime() + 1000;
                scheduleTimer = setTimeout(() => {
                    stopTracking();
                    scheduleNextCheck();
                }, diff);
            }
        };

        const syncTracking = async () => {
            if (isCancelled) return;

            // Strict check: if outside tracking window, do not connect and ensure live position is cleared
            if (!isTrackingActive()) {
                stopTracking();
                scheduleNextCheck();
                return;
            }

            if (connection) return;

            const hubUrl = getSignalRHubUrl();

            const newConnection = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets
                })
                .withAutomaticReconnect()
                .build();

            newConnection.on("ReceiveGpsLog", function (gpsData) {
                if (isCancelled) return;
                if (!isTrackingActive()) {
                    stopTracking();
                    scheduleNextCheck();
                    return;
                }
                if (gpsData && gpsData.latitude && gpsData.longitude) {
                    const parsedLat = parseNMEACoordinate(gpsData.latitude);
                    const parsedLng = parseNMEACoordinate(gpsData.longitude);
                    const newPos = {
                        lat: parsedLat,
                        lng: parsedLng,
                        course: gpsData.course ? parseFloat(gpsData.course) : 0
                    };
                    setTargetBusPosition(newPos);
                    setLiveBusPath(prev => [...prev.slice(-49), newPos]);
                }
            });

            newConnection.onreconnected(() => {
                if (!isCancelled && deviceMac && isTrackingActive()) {
                    newConnection.invoke("RequestGpsData", deviceMac).catch(() => { });
                }
            });

            try {
                await newConnection.start();
                if (isCancelled || !isTrackingActive()) {
                    newConnection.stop().catch(() => { });
                    return;
                }
                connection = newConnection;
                newConnection.invoke("RequestGpsData", deviceMac).catch(() => { });
            } catch (e) {
                console.error("Pickup SignalR Connection Error: ", e);
            }
        };

        const intervalCheck = setInterval(() => {
            if (isTrackingActive()) {
                if (!connection) {
                    syncTracking();
                }
            } else {
                if (connection || targetBusPosition || liveBusPosition) {
                    stopTracking();
                }
            }
        }, 30000);

        if (isTrackingActive()) {
            syncTracking();
            scheduleNextCheck();
        } else {
            stopTracking();
            scheduleNextCheck();
        }

        return () => {
            isCancelled = true;
            if (scheduleTimer) clearTimeout(scheduleTimer);
            if (intervalCheck) clearInterval(intervalCheck);
            if (connection) {
                try { connection.stop(); } catch (e) { }
                connection = null;
            }
        };
    }, [isTrackingActive, transportationData?.GpsDeviceID]);

    const isArrived = React.useMemo(() => {
        if (hasArrived) return true;
        const currentRef = liveBusPosition || targetBusPosition;
        if (!currentRef || !transportationData || stops.length === 0) return false;
        try {
            const rawPoints = stops.map(s => s.raw);
            const isNearStop = (stop, maxMeters = 300) => {
                if (!stop || !stop.Latitude || !stop.Longitude) return false;
                const d = getDistanceInMeters(
                    currentRef.lat,
                    currentRef.lng,
                    parseFloat(stop.Latitude),
                    parseFloat(stop.Longitude)
                );
                return d <= maxMeters;
            };

            const studentStop = rawPoints.find(
                (p) =>
                    p.PointName &&
                    transportationData.PickupPointName &&
                    (p.PointName.trim().toLowerCase() === transportationData.PickupPointName.trim().toLowerCase() ||
                        p.PointName.toLowerCase().includes(transportationData.PickupPointName.toLowerCase()) ||
                        transportationData.PickupPointName.toLowerCase().includes(p.PointName.toLowerCase()))
            );
            const studentStopIndex = studentStop ? rawPoints.indexOf(studentStop) : -1;

            const finalStop = rawPoints[rawPoints.length - 1];
            if (isNearStop(finalStop)) return true;
            if (studentStop && studentStopIndex >= 0 && isNearStop(studentStop)) return true;

            let closestIndex = 0;
            let minDistance = Infinity;
            rawPoints.forEach((p, idx) => {
                const d = getDistanceInMeters(
                    currentRef.lat,
                    currentRef.lng,
                    parseFloat(p.Latitude),
                    parseFloat(p.Longitude)
                );
                if (d < minDistance) {
                    minDistance = d;
                    closestIndex = idx;
                }
            });

            const targetIndex = studentStopIndex >= 0 ? studentStopIndex : rawPoints.length - 1;
            if (targetIndex >= 0 && closestIndex >= targetIndex && minDistance <= 300) return true;

            return false;
        } catch (e) {
            return false;
        }
    }, [hasArrived, liveBusPosition, targetBusPosition, transportationData, stops]);

    React.useEffect(() => {
        if (isArrived && !hasArrived) {
            setHasArrived(true);
        }
    }, [isArrived, hasArrived]);

    const fullPath = React.useMemo(() => {
        return (pickupMarker && pickupMarker.length > 0)
            ? pickupMarker
            : stops.map(s => s.position);
    }, [pickupMarker, stops]);

    const showLive = Boolean(targetBusPosition || liveBusPosition) && isTrackingActive();

    const segments = React.useMemo(() => {
        if (!showLive || !fullPath || fullPath.length === 0) return null;
        if (isArrived) return { coveredPath: fullPath, remainingPath: [] };
        const currentRef = liveBusPosition || targetBusPosition;
        if (!currentRef) return { coveredPath: [], remainingPath: fullPath };

        let closestIdx = 0;
        let minDist = Infinity;
        for (let i = 0; i < fullPath.length; i++) {
            const dLat = currentRef.lat - fullPath[i].lat;
            const dLng = currentRef.lng - fullPath[i].lng;
            const d = dLat * dLat + dLng * dLng;
            if (d < minDist) {
                minDist = d;
                closestIdx = i;
            }
        }

        const currentPos = { lat: currentRef.lat, lng: currentRef.lng };
        const coveredPath = closestIdx > 0
            ? [...fullPath.slice(0, closestIdx + 1), currentPos]
            : [fullPath[0], currentPos];
        const remainingPath = closestIdx < fullPath.length - 1
            ? [currentPos, ...fullPath.slice(closestIdx + 1)]
            : [currentPos, fullPath[fullPath.length - 1]];

        return { coveredPath, remainingPath };
    }, [showLive, fullPath, isArrived, liveBusPosition, targetBusPosition]);

    return (
        <Map
            google={google}
            zoom={11}
            style={MapStyle}
            initialCenter={initialCenter}
        >
            {stops.map((stop) => (
                <Marker
                    key={stop.id}
                    position={stop.position}
                    label={stop.label}
                    title={stop.title}
                />
            ))}
            {showLive && segments?.coveredPath && segments.coveredPath.length > 1 ? (
                <Polyline
                    key="pickup-covered-line"
                    path={segments.coveredPath}
                    strokeColor="#A0AEC0"
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            ) : null}
            {showLive && segments?.remainingPath && segments.remainingPath.length > 1 ? (
                <Polyline
                    key="pickup-remaining-line"
                    path={segments.remainingPath}
                    strokeColor="#007bff"
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            ) : null}
            {showLive && targetBusPosition ? (
                <SmoothLiveBusMarker
                    targetPosition={targetBusPosition}
                    liveBusPath={liveBusPath}
                    windowWidth={windowWidth}
                    onPositionUpdate={handlePositionUpdate}
                />
            ) : null}
            {!showLive && fullPath && fullPath.length > 0 ? (
                <Polyline
                    key="pickup-static-line"
                    path={fullPath}
                    strokeColor="#007bff"
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            ) : null}
        </Map>
    );
});

/**
 * Isolated Drop Route Map Component.
 * ONLY connects to SignalR and updates when Drop route time is active.
 * When it is Pickup time, this component NEVER calls SignalR, receives no updates,
 * and renders as a completely normal static route without reloading or blinking.
 */
const DropRouteMap = React.memo(function DropRouteMap({
    google,
    transportationData,
    dropMarker,
    initialCenter,
    windowWidth,
}) {
    const [targetBusPosition, setTargetBusPosition] = React.useState(null);
    const [liveBusPosition, setLiveBusPosition] = React.useState(null);
    const [liveBusPath, setLiveBusPath] = React.useState([]);
    const [hasArrived, setHasArrived] = React.useState(false);

    const handlePositionUpdate = React.useCallback((pos) => {
        setLiveBusPosition(pos);
    }, []);

    const stops = React.useMemo(() => {
        try {
            const raw = transportationData?.DropRoutePoint;
            const list = raw ? JSON.parse(raw) : [];
            return list.map((point, index) => ({
                id: `drop-stop-${index}`,
                position: {
                    lat: parseFloat(point.Latitude),
                    lng: parseFloat(point.Longitude)
                },
                label: (index + 1).toString(),
                title: point.PointName,
                raw: point
            }));
        } catch (e) {
            return [];
        }
    }, [transportationData?.DropRoutePoint]);

    const isTrackingActive = React.useCallback(() => {
        if (!transportationData?.BusRouteForDropStartTime || !transportationData?.BusRouteForDropEndTime) return false;
        return isWithinTrackingTime(transportationData.BusRouteForDropStartTime, transportationData.BusRouteForDropEndTime);
    }, [transportationData?.BusRouteForDropStartTime, transportationData?.BusRouteForDropEndTime]);

    // ONLY initiates SignalR connection when Drop tracking is active
    React.useEffect(() => {
        const deviceMac = transportationData?.GpsDeviceID || transportationData?.DeviceMacNo || transportationData?.RTORegNoForDrop || transportationData?.RTORegNoForPickup;
        if (!deviceMac) {
            setTargetBusPosition(null);
            setLiveBusPosition(null);
            setLiveBusPath([]);
            return;
        }

        let connection = null;
        let scheduleTimer = null;
        let isCancelled = false;

        const stopTracking = () => {
            if (connection) {
                try { connection.stop(); } catch (e) { }
                connection = null;
            }
            setTargetBusPosition(null);
            setLiveBusPosition(null);
            setLiveBusPath([]);
        };

        const scheduleNextCheck = () => {
            if (scheduleTimer) {
                clearTimeout(scheduleTimer);
                scheduleTimer = null;
            }
            const now = new Date();
            const win = getTrackingWindow(transportationData?.BusRouteForDropStartTime, transportationData?.BusRouteForDropEndTime);
            if (!win) return;

            if (win.start > now) {
                const diff = win.start.getTime() - now.getTime();
                if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
                    scheduleTimer = setTimeout(() => {
                        syncTracking();
                        scheduleNextCheck();
                    }, diff);
                }
            } else if (now >= win.start && now <= win.end) {
                const diff = win.end.getTime() - now.getTime() + 1000;
                scheduleTimer = setTimeout(() => {
                    stopTracking();
                    scheduleNextCheck();
                }, diff);
            }
        };

        const syncTracking = async () => {
            if (isCancelled) return;

            // Strict check: if outside tracking window, do not connect and ensure live position is cleared
            if (!isTrackingActive()) {
                stopTracking();
                scheduleNextCheck();
                return;
            }

            if (connection) return;

            const hubUrl = getSignalRHubUrl();

            const newConnection = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets
                })
                .withAutomaticReconnect()
                .build();

            newConnection.on("ReceiveGpsLog", function (gpsData) {
                if (isCancelled) return;
                if (!isTrackingActive()) {
                    stopTracking();
                    scheduleNextCheck();
                    return;
                }
                if (gpsData && gpsData.latitude && gpsData.longitude) {
                    const parsedLat = parseNMEACoordinate(gpsData.latitude);
                    const parsedLng = parseNMEACoordinate(gpsData.longitude);
                    const newPos = {
                        lat: parsedLat,
                        lng: parsedLng,
                        course: gpsData.course ? parseFloat(gpsData.course) : 0
                    };
                    setTargetBusPosition(newPos);
                    setLiveBusPath(prev => [...prev.slice(-49), newPos]);
                }
            });

            newConnection.onreconnected(() => {
                if (!isCancelled && deviceMac && isTrackingActive()) {
                    newConnection.invoke("RequestGpsData", deviceMac).catch(() => { });
                }
            });

            try {
                await newConnection.start();
                if (isCancelled || !isTrackingActive()) {
                    newConnection.stop().catch(() => { });
                    return;
                }
                connection = newConnection;
                newConnection.invoke("RequestGpsData", deviceMac).catch(() => { });
            } catch (e) {
                console.error("Drop SignalR Connection Error: ", e);
            }
        };

        const intervalCheck = setInterval(() => {
            if (isTrackingActive()) {
                if (!connection) {
                    syncTracking();
                }
            } else {
                if (connection || targetBusPosition || liveBusPosition) {
                    stopTracking();
                }
            }
        }, 30000);

        if (isTrackingActive()) {
            syncTracking();
            scheduleNextCheck();
        } else {
            stopTracking();
            scheduleNextCheck();
        }

        return () => {
            isCancelled = true;
            if (scheduleTimer) clearTimeout(scheduleTimer);
            if (intervalCheck) clearInterval(intervalCheck);
            if (connection) {
                try { connection.stop(); } catch (e) { }
                connection = null;
            }
        };
    }, [isTrackingActive, transportationData?.GpsDeviceID, transportationData?.DeviceMacNo, transportationData?.RTORegNoForDrop, transportationData?.RTORegNoForPickup, transportationData?.BusRouteForDropStartTime, transportationData?.BusRouteForDropEndTime]);

    const isArrived = React.useMemo(() => {
        if (hasArrived) return true;
        const currentRef = liveBusPosition || targetBusPosition;
        if (!currentRef || !transportationData || stops.length === 0) return false;
        try {
            const rawPoints = stops.map(s => s.raw);
            const isNear = (pt, maxMeters = 300) => {
                if (!pt) return false;
                const lat = pt.lat !== undefined ? pt.lat : parseFloat(pt.Latitude);
                const lng = pt.lng !== undefined ? pt.lng : parseFloat(pt.Longitude);
                if (isNaN(lat) || isNaN(lng)) return false;
                return getDistanceInMeters(currentRef.lat, currentRef.lng, lat, lng) <= maxMeters;
            };

            const startPoint = dropMarker && dropMarker.length > 0 ? dropMarker[0] : rawPoints[0];
            const endPoint = dropMarker && dropMarker.length > 0 ? dropMarker[dropMarker.length - 1] : rawPoints[rawPoints.length - 1];

            // If bus is still at the school origin before trip progress, it has not arrived
            const nearOrigin = isNear(startPoint, 250);
            if (nearOrigin) return false;

            // Check if bus reached the final terminus of the entire drop route
            const nearTripDest = isNear(endPoint, 300);
            if (nearTripDest) return true;

            // Find this student's specific drop stop
            const studentStop = rawPoints.find(
                (p) =>
                    p.PointName &&
                    transportationData.DropPointName &&
                    (p.PointName.trim().toLowerCase() === transportationData.DropPointName.trim().toLowerCase() ||
                        p.PointName.toLowerCase().includes(transportationData.DropPointName.toLowerCase()) ||
                        transportationData.DropPointName.toLowerCase().includes(p.PointName.toLowerCase()))
            );
            const studentStopIndex = studentStop ? rawPoints.indexOf(studentStop) : -1;

            if (studentStop && studentStopIndex >= 0 && isNear(studentStop, 300)) return true;

            let closestStopIndex = 0;
            let minDist = Infinity;
            rawPoints.forEach((p, idx) => {
                const d = getDistanceInMeters(
                    currentRef.lat,
                    currentRef.lng,
                    parseFloat(p.Latitude),
                    parseFloat(p.Longitude)
                );
                if (d < minDist) {
                    minDist = d;
                    closestStopIndex = idx;
                }
            });

            const targetIdx = studentStopIndex >= 0 ? studentStopIndex : rawPoints.length - 1;
            if (targetIdx >= 0 && closestStopIndex >= targetIdx && minDist <= 300) return true;

            return false;
        } catch (e) {
            return false;
        }
    }, [hasArrived, liveBusPosition, targetBusPosition, transportationData, stops, dropMarker]);

    React.useEffect(() => {
        if (isArrived && !hasArrived) {
            setHasArrived(true);
        }
    }, [isArrived, hasArrived]);

    const fullPath = React.useMemo(() => {
        return (dropMarker && dropMarker.length > 0)
            ? dropMarker
            : stops.map(s => s.position);
    }, [dropMarker, stops]);

    const showLive = Boolean(targetBusPosition || liveBusPosition) && isTrackingActive();

    const segments = React.useMemo(() => {
        if (!showLive || !fullPath || fullPath.length === 0) return null;
        if (isArrived) return { coveredPath: fullPath, remainingPath: [] };
        const currentRef = liveBusPosition || targetBusPosition;
        if (!currentRef) return { coveredPath: [], remainingPath: fullPath };

        let closestIdx = 0;
        let minDist = Infinity;
        for (let i = 0; i < fullPath.length; i++) {
            const dLat = currentRef.lat - fullPath[i].lat;
            const dLng = currentRef.lng - fullPath[i].lng;
            const d = dLat * dLat + dLng * dLng;
            if (d < minDist) {
                minDist = d;
                closestIdx = i;
            }
        }

        const currentPos = { lat: currentRef.lat, lng: currentRef.lng };
        const coveredPath = closestIdx > 0
            ? [...fullPath.slice(0, closestIdx + 1), currentPos]
            : [fullPath[0], currentPos];
        const remainingPath = closestIdx < fullPath.length - 1
            ? [currentPos, ...fullPath.slice(closestIdx + 1)]
            : [currentPos, fullPath[fullPath.length - 1]];

        return { coveredPath, remainingPath };
    }, [showLive, fullPath, isArrived, liveBusPosition, targetBusPosition]);

    return (
        <Map
            google={google}
            zoom={11}
            style={MapStyle}
            initialCenter={initialCenter}
        >
            {stops.map((stop) => (
                <Marker
                    key={stop.id}
                    position={stop.position}
                    label={stop.label}
                    title={stop.title}
                />
            ))}
            {showLive && segments?.coveredPath && segments.coveredPath.length > 1 ? (
                <Polyline
                    key="drop-covered-line"
                    path={segments.coveredPath}
                    strokeColor="#A0AEC0"
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            ) : null}
            {showLive && segments?.remainingPath && segments.remainingPath.length > 1 ? (
                <Polyline
                    key="drop-remaining-line"
                    path={segments.remainingPath}
                    strokeColor="#007bff"
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            ) : null}
            {showLive && targetBusPosition ? (
                <SmoothLiveBusMarker
                    targetPosition={targetBusPosition}
                    liveBusPath={liveBusPath}
                    windowWidth={windowWidth}
                    onPositionUpdate={handlePositionUpdate}
                />
            ) : null}
            {!showLive && fullPath && fullPath.length > 0 ? (
                <Polyline
                    key="drop-static-line"
                    path={fullPath}
                    strokeColor="#007bff"
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            ) : null}
        </Map>
    );
});

function Profile(props) {
    const history = useHistory();

    let data = commonService.getLoginUserData();
    const ChartBackgroundColor = "#10AB4A";
    const ChartDatasetsLabel = "Attendence";
    const ChartFontColor = {
        maintainAspectRatio: false,
        Plugins: {
            labels: {
                fontColor: "#ffffff",
            },
        },
        options: {
            scales: {
                x: {
                    ticks: {
                        color: "red",
                    },
                },
            },
        },
    };
    const [attendenceByMonth, setattendenceByMonth] = useState({
        IsData: false,
        data: [],
    });
    const [attendenceSummary, setattendenceSummary] = useState({
        IsData: false,
        data: [],
    });
    const [basicDetails, setbasicDetails] = useState({ IsData: false, data: [] });
    const [hostelDetails, sethostelDetails] = useState({
        IsData: false,
        data: [],
    });
    const [transportationDetails, settransportationDetails] = useState({
        IsData: false,
        data: [],
    });
    const [PickupMapinitialCenter, setPickupMapinitialCenter] = useState({
        lat: 23.0389,
        lng: 72.64032,
    });
    const [DropMapinitialCenter, setDropMapinitialCenter] = useState({
        lat: 23.0389,
        lng: 72.64032,
    });

    const [PickupMarker, setPickupMarker] = useState([]);
    const [DropMarker, setDropMarker] = useState([]);
    const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);

    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [Exam, setExam] = useState({ IsData: false, data: [] });
    const [Fees, setFees] = useState({ IsData: false, data: [] });

    const [Chartdata, setChartdata] = useState({
        labels: [],
        datasets: [
            {
                label: ChartDatasetsLabel,
                data: [],
                backgroundColor: [ChartBackgroundColor],
            },
        ],
    });
    const [StudentDetailType, setStudentDetailType] = useState("");

    function BindProfileData() {
        const req = {
            method: "POST",
            headers: commonService.getHeaders(),
        };
        fetch(AppConfigData.APIBasePath + "v1/user/getprofilebymember", req)
            .then((response) => response.json())
            .then(function (json) {
                if (json.status_code === 1) {
                    setattendenceByMonth({
                        IsData: true,
                        data: json.data.attendenceByMonth,
                    });
                    setattendenceSummary({
                        IsData: true,
                        data: json.data.attendenceSummary,
                    });
                    setbasicDetails({ IsData: true, data: json.data.basicDetails });
                    sethostelDetails({ IsData: true, data: json.data.hostelDetails });
                    settransportationDetails({
                        IsData: true,
                        data: json.data.transportationDetails,
                    });

                    if (json.data.attendenceByMonth.length !== 0) {
                        let labels = [];
                        let data = [];
                        for (let i = 0; i < json.data.attendenceByMonth.length; i++) {
                            labels.push(
                                json.data.attendenceByMonth[i].MonthName.substr(0, 3) +
                                "-" +
                                json.data.attendenceByMonth[i].YEAR
                            );
                            data.push(json.data.attendenceByMonth[i].DataCount);
                        }
                        setChartdata({
                            labels: labels,
                            datasets: [
                                {
                                    label: ChartDatasetsLabel,
                                    data: data,
                                    backgroundColor: [ChartBackgroundColor],
                                },
                            ],
                        });
                    }

                    if (json.data.transportationDetails.length !== 0) {
                        const decodePolyline = require("decode-google-map-polyline");
                        if (
                            json.data.transportationDetails[0].PolyLineForPickup !== "" &&
                            json.data.transportationDetails[0].PolyLineForPickup
                        ) {
                            setPickupMarker(
                                decodePolyline(
                                    json.data.transportationDetails[0].PolyLineForPickup
                                )
                            );
                            let Array = decodePolyline(
                                json.data.transportationDetails[0].PolyLineForPickup
                            );
                            setPickupMapinitialCenter(Array[0]);
                        }

                        if (
                            json.data.transportationDetails[0].PolyLineForDrop !== "" &&
                            json.data.transportationDetails[0].PolyLineForDrop
                        ) {
                            setDropMarker(
                                decodePolyline(
                                    json.data.transportationDetails[0].PolyLineForDrop
                                )
                            );
                            let Array = decodePolyline(
                                json.data.transportationDetails[0].PolyLineForDrop
                            );
                            setDropMapinitialCenter(Array[0]);
                        }
                    }
                } else if (json.status_code === 0) {
                    setattendenceByMonth({ IsData: true, data: [] });
                    setattendenceSummary({ IsData: true, data: [] });
                    setbasicDetails({ IsData: true, data: [] });
                    sethostelDetails({ IsData: true, data: [] });
                    settransportationDetails({ IsData: true, data: [] });
                } else if (json.status_code === 2) {
                    localStorage.clear();
                    history.push("/");
                }
            });
    }

    function BindExamList() {
        setExam({ IsData: false, data: [] });

        const reqExam = {
            method: "POST",
            headers: commonService.getHeaders(),
            body: JSON.stringify({
                saveType: "Send",
                searchText: "",
                fromDate: data[0].StartDate,
                toDate: new Date(),
                requestFrom: "Web",
                noOfData: 3,
            }),
        };
        fetch(AppConfigData.APIBasePath + "v1/exam/getlistbymember", reqExam)
            .then((response) => response.json())
            .then(function (json) {
                if (json.status_code === 1) {
                    setExam({ IsData: true, data: json.data });
                } else if (json.status_code === 0) {
                    setExam({ IsData: true, data: [] });
                } else if (json.status_code === 2) {
                    localStorage.clear();
                    history.push("/");
                }
            });
    }

    function BindFeesList() {
        setFees({ IsData: false, data: [] });

        const reqExam = {
            method: "POST",
            headers: commonService.getHeaders(),
        };
        fetch(AppConfigData.APIBasePath + "v1/fees/getdetailsbymember", reqExam)
            .then((response) => response.json())
            .then(function (json) {
                if (json.status_code === 1) {
                    setFees({ IsData: true, data: json.data });
                } else if (json.status_code === 0) {
                    setFees({ IsData: true, data: [] });
                } else if (json.status_code === 2) {
                    localStorage.clear();
                    history.push("/");
                }
            });
    }

    React.useEffect(() => {

        BindProfileData();
        BindExamList();
        BindFeesList();

    }, []);

    return (
        <>
            <h1 className="page-header my-4 uppercase">
                {data[0].FirstName} {data[0].LastName}
            </h1>
            <div className="row">
                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-3  col-xl-3 left-side-student-profile mb-3">
                    <div className="card student-main-profile">
                        <div className="student-profile-edit">
                            <div className="student-id">
                                {data[0].MemberType.toLowerCase() !== "student" ? (
                                    <label className="student-nameId">
                                        {commonService.LabelDisplayText("EMPCode")}{" "}
                                        <span>
                                            {basicDetails.data.length !== 0 &&
                                                basicDetails.data[0].GRNo}
                                        </span>
                                    </label>
                                ) : (
                                    <label className="student-nameId">
                                        {commonService.LabelDisplayText("GRScholar")}{" "}
                                        <span>
                                            {basicDetails.data.length !== 0 &&
                                                basicDetails.data[0].GRNo}
                                        </span>
                                    </label>
                                )}
                            </div>
                        </div>
                        <div className="student-photo-profile mt-4">
                            <div
                                className="profile-area-main"
                                style={{
                                    backgroundColor: commonService.CreateColor(data[0].Initials)
                                        .BGColor,
                                    color: commonService.CreateColor(data[0].Initials).FontColor,
                                }}
                            >
                                {data[0].Avatar !== "" ? (
                                    <img src={AppConfigData.ERPFileBasePath + data[0].Avatar} />
                                ) : (
                                    data[0].Initials
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="std-classNameroom-detail mt-4 mb-4">
                        <div className="student-classNameroom-id">
                            {basicDetails.data.length && basicDetails.data[0].UID && (
                                <label className="student-id-label">
                                    {basicDetails.data[0].UID}
                                </label>
                            )}
                        </div>
                        <div className="student-divison mt-2">
                            <h2>
                                {basicDetails.data.length !== 0 &&
                                    basicDetails.data[0].STDDIVDEPT}
                            </h2>
                        </div>
                        <div className="student-roll-no mt-1">
                            {basicDetails.data.length && basicDetails.data[0].RollNo && (
                                <>
                                    {commonService.LabelDisplayText("Roll")}{" "}
                                    <label className="roll-number">
                                        {" "}
                                        {basicDetails.data[0].RollNo}
                                    </label>
                                </>
                            )}
                        </div>
                    </div>
                    {
                        data[0].MemberType.toLowerCase() === "teacher" ? (
                            <div className="row attendence-leaderboard">
                                <div className="col-6 student-attendence">
                                    <h2 className="student-attendence-percentage">
                                        {
                                            attendenceSummary.data.length !== 0 &&
                                            attendenceSummary.data[0].NoOfPresentInPer
                                        }
                                        %
                                    </h2>
                                    <p className="student-attendence-leaderboard">
                                        {commonService.LabelDisplayText("MenuAttendance")}
                                    </p>
                                </div>
                                <div className="col-6 student-leaderbaord">
                                    <img src="achivement.svg" />
                                    <h2 className="student-attendence-percentage student-achivement-percentage">
                                        {basicDetails.data.length !== 0 &&
                                            basicDetails.data[0].CreditStar}
                                    </h2>
                                    <p className="student-attendence-leaderboard student-achivement-leader">
                                        {commonService.LabelDisplayText("Leaderboard")}
                                    </p>
                                </div>
                            </div>) : (
                            <>
                                <div className="right-sidebar ">
                                    <div style={{ width: "100%" }}>
                                        <ul style={{ listStyle: "none" }}>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("")}>360 View </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("STUDENT")}>Basic Information </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("PARENT")}>Parent  </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("SIBLING")}>Sibling  </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("FEE")}>Fee </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("EXAM")}>Exam </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("TRANSPORTATION")}>Transportation </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("HEALTH")}>Health </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("DOCUMNETS")}>Documnets </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("REWARD")}>Reward </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("OTHER INFORMATION")}>Other Information </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("CHILD CUSTODY")}>Child Custody </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("SUBJECT ENROLLMENT")}>Subject Enrollment </a></div></li>
                                            <li style={{ textAlign: "left", padding: "5px 5px", fontSize: "15px" }}><div style={{ borderBottom: "1px solid #d8d8d8" }}><a onClick={() => setStudentDetailType("HOSTEL DETAILS")}>Hostel Details </a></div></li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )
                    }

                    {hostelDetails.data.length !== 0 && commonService.getLoginRoleData("Hostel", "IsView") && (
                        <div className="student-hostel mt-4">
                            <h5>{commonService.LabelDisplayText("Hostel")}</h5>
                            <div className="hostel-information mt-2">
                                <div className="student-hostel-number">
                                    <p className="room-number">
                                        {commonService.LabelDisplayText("BedHostel")}{" "}
                                        <span> {hostelDetails.data[0].BedCode}</span>
                                    </p>
                                    <label className="hostel-room-rent">
                                        {hostelDetails.data[0].BedType_Term}
                                    </label>
                                </div>
                                <div className="student-hostel-number">
                                    <p className="room-number">
                                        {commonService.LabelDisplayText("RoomHostel")}{" "}
                                        <span> {hostelDetails.data[0].RoomNo}</span>
                                    </p>
                                    <label className="hostel-room-rent">
                                        {hostelDetails.data[0].RoomTypeName}
                                    </label>
                                </div>
                                <div className="student-hostel-number">
                                    <p className="room-number">
                                        {commonService.LabelDisplayText("FloorHostel")}{" "}
                                        <span> {hostelDetails.data[0].FloorName}</span>
                                    </p>
                                </div>
                                <div className="student-hostel-number">
                                    <p className="room-number">
                                        {commonService.LabelDisplayText("BuildingHostel")}{" "}
                                        <span> {hostelDetails.data[0].BuildingName}</span>
                                    </p>
                                </div>
                                {/*<div className="rector-name-number">*/}
                                {/*    <p className="room-number">Bed <span> {hostelDetails.data[0].BedCode}</span> Type <span>{hostelDetails.data[0].BedType_Term}</span></p>*/}
                                {/*</div>*/}
                                <div className="hostel-other-information mt-1">
                                    <label className="other-information-hostel">
                                        {commonService.LabelDisplayText("HR")} {hostelDetails.data[0].HRNo}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="hostel-address mt-4">
                        {basicDetails.data.length !== 0 ? (
                            <>
                                <div className="location-icon mr-2">
                                    <i className="fa-regular fa-location-dot"></i>
                                </div>
                                <div className="address-location">
                                    {basicDetails.data[0].Add1 === "" && (
                                        <p>{commonService.LabelDisplayText("NoAddressDetails")}</p>
                                    )}
                                    <p>
                                        {basicDetails.data.length !== 0 &&
                                            basicDetails.data[0].Add1 +
                                            " " +
                                            basicDetails.data[0].City +
                                            " " +
                                            basicDetails.data[0].Taluka +
                                            " " +
                                            basicDetails.data[0].Distict +
                                            " " +
                                            basicDetails.data[0].State +
                                            " " +
                                            basicDetails.data[0].Country +
                                            " " +
                                            basicDetails.data[0].PinCode}
                                    </p>
                                </div>{" "}
                            </>
                        ) : (
                            <>
                                <div className="location-icon mr-2">
                                    <i className="fa-regular fa-location-dot"></i>
                                </div>
                                <div className="address-location">{commonService.LabelDisplayText("NoAddressDetails")}</div>
                            </>
                        )}
                    </div>
                </div>
                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-9 col-xl-9 right-side-student-profile pr-0">

                    {
                        StudentDetailType.toUpperCase() === "STUDENT" ? <BasicInformationView /> :
                            StudentDetailType.toUpperCase() === "PARENT" ? <ParentView /> :
                                StudentDetailType.toUpperCase() === "SIBLING" ? <SiblingView /> :
                                    StudentDetailType.toUpperCase() === "FEE" ? <FeeView /> :
                                        StudentDetailType.toUpperCase() === "EXAM" ? <ExamView /> :
                                            StudentDetailType.toUpperCase() === "TRANSPORTATION" ? <TransportationView /> :
                                                StudentDetailType.toUpperCase() === "HEALTH" ? <Healthview /> :
                                                    StudentDetailType.toUpperCase() === "OTHER INFORMATION" ? <OtherInfoView /> :
                                                        StudentDetailType.toUpperCase() === "CHILD CUSTODY" ? <ChildCustodyView /> :
                                                            StudentDetailType.toUpperCase() === "REWARD" ? <RewardView /> :
                                                                StudentDetailType.toUpperCase() === "SUBJECT ENROLLMENT" ? <SubjectEnrollmentView /> :
                                                                    StudentDetailType.toUpperCase() === "HOSTEL DETAILS" ? <HostelDetailView /> :
                                                                        StudentDetailType.toUpperCase() === "DOCUMNETS" ? <DocumentView /> :
                                                                            <>
                                                                                {
                                                                                    commonService.getLoginRoleData("Attendance", "IsView") &&
                                                                                    <>{!attendenceSummary.IsData ? (
                                                                                        <div className="bar-chart">
                                                                                            <_Skeleton type={_SkeletonType.Line} count={6} lineheight={30} />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="bar-chart">
                                                                                            <div className="attendence-title-ratio">
                                                                                                <h5>{commonService.LabelDisplayText("MenuAttendance")}</h5>
                                                                                                <p className="attendence-ratio">
                                                                                                    <span className="ratio-attendence">
                                                                                                        {attendenceSummary.data.length !== 0 &&
                                                                                                            attendenceSummary.data[0].NoOfPresentDays +
                                                                                                            "/" +
                                                                                                            attendenceSummary.data[0].NoOfDays}
                                                                                                    </span>
                                                                                                    {commonService.LabelDisplayText("Days")}
                                                                                                </p>
                                                                                            </div>
                                                                                            <Bar
                                                                                                data={Chartdata}
                                                                                                width={100}
                                                                                                height={50}
                                                                                                options={ChartFontColor}
                                                                                            />
                                                                                        </div>
                                                                                    )}</>
                                                                                }
                                                                                {
                                                                                    commonService.getLoginRoleData("Exam", "IsView") &&
                                                                                    <div className="finish-exams">
                                                                                        <div className="inner-header-for-section mt-4 mb-3">
                                                                                            <h2 className="m-0">{commonService.LabelDisplayText("FinishExam")}({
                                                                                                Exam.IsData ? <>{Exam.data.length}</> : <>0</>
                                                                                            })</h2>
                                                                                            <Link to="/exam">{commonService.LabelDisplayText("ViewAll")}</Link>
                                                                                        </div>
                                                                                        <div className="row">
                                                                                            {!Exam.IsData ? (
                                                                                                <div className="col-sm-12"> <_Skeleton
                                                                                                    type={_SkeletonType.Exam}
                                                                                                    count={1}
                                                                                                    lineheight={40}
                                                                                                /></div>

                                                                                            ) : Exam.data.length === 0 ? (
                                                                                                <_NoDataFound fontsize={"150px"} />
                                                                                            ) : (
                                                                                                Exam.data.map(
                                                                                                    (item, index) =>
                                                                                                        new Date(item.DateOfExam) <= new Date() && (
                                                                                                            <div
                                                                                                                className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-4 exam-section-area mb-3"
                                                                                                                key={index}
                                                                                                            >
                                                                                                                <a
                                                                                                                    className={`pointer-cursor`}

                                                                                                                >
                                                                                                                    <div className="section">
                                                                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                                                                            <label>
                                                                                                                                {commonService.getDateInFormat(item.DateOfExam)}
                                                                                                                            </label>
                                                                                                                            <label>{commonService.LabelDisplayText("TotalMark")} {item.TotakMarks}</label>
                                                                                                                        </div>
                                                                                                                        <h3 className="science-text">
                                                                                                                            <div className="d-flex align-items-center justify-content-center">{item.IsOnlineExam ? (
                                                                                                                                <i className="fa-solid fa-laptop-mobile mr-2"></i>
                                                                                                                            ) : (
                                                                                                                                <i className="fa-regular fa-school mr-2"></i>
                                                                                                                            )}
                                                                                                                                {item.SubjectName}</div>
                                                                                                                            <label className="d-flex align-items-center justify-content-center">{item.ExamTypeAndPaperType}</label>
                                                                                                                        </h3>
                                                                                                                        <div className="d-flex justify-content-between align-items-cente ">
                                                                                                                            {data[0].MemberType.toLowerCase() !== "student" ? (
                                                                                                                                <label className="mark-label float-left">
                                                                                                                                    {commonService.LabelDisplayText("STD")} : {item.StandardName} - {item.ExamName}
                                                                                                                                </label>
                                                                                                                            ) : (
                                                                                                                                <label className="mark-label float-left">
                                                                                                                                    {item.ExamName}
                                                                                                                                </label>
                                                                                                                            )}
                                                                                                                            <label>{item.ExamOfTypeTerm}</label>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </a>
                                                                                                            </div>
                                                                                                        )
                                                                                                )
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                }

                                                                                {
                                                                                    commonService.getLoginRoleData("Fees", "IsView") &&
                                                                                    <>{data[0].MemberType.toUpperCase() !== "TEACHER" && (
                                                                                        <div className="fees-status">
                                                                                            <div className="inner-header-for-section mt-4 mb-3">
                                                                                                <h2 className="m-0">{commonService.LabelDisplayText("MenuFees")}</h2>
                                                                                                <Link to="/fees">{commonService.LabelDisplayText("ViewAll")}</Link>
                                                                                            </div>
                                                                                            <div className="row">
                                                                                                {!Fees.IsData ? (
                                                                                                    <>
                                                                                                        {" "}
                                                                                                        <div className="col-6">
                                                                                                            <div className="student-term-fees">
                                                                                                                <_Skeleton
                                                                                                                    type={_SkeletonType.Line}
                                                                                                                    count={1}
                                                                                                                    lineheight={40}
                                                                                                                />
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div className="col-6">
                                                                                                            <div className="student-term-fees">
                                                                                                                <_Skeleton
                                                                                                                    type={_SkeletonType.Line}
                                                                                                                    count={1}
                                                                                                                    lineheight={40}
                                                                                                                />
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </>
                                                                                                ) : Fees.data.feesDetails.length === 0 ? (
                                                                                                    <_NoDataFound fontsize={"100px"} />
                                                                                                ) : (
                                                                                                    Fees.data.feesDetails.map(
                                                                                                        (item, index) =>
                                                                                                            index < 2 && (
                                                                                                                <div
                                                                                                                    className="col-xs-12 col-sm-12 col-md-6 col-lg-6 mb-3"
                                                                                                                    key={index}
                                                                                                                >
                                                                                                                    <div className="student-term-fees">
                                                                                                                        <div className="first-term">
                                                                                                                            <p>{item.DisplayName}</p>
                                                                                                                        </div>
                                                                                                                        <div className="term-fees-status">
                                                                                                                            <p>
                                                                                                                                {item.Amount}/-
                                                                                                                                {item.UnpaidAmount > 0 ? (
                                                                                                                                    <span className="fess-paid-pending">
                                                                                                                                        {" "}
                                                                                                                                        {commonService.LabelDisplayText("Unpaid")}
                                                                                                                                    </span>
                                                                                                                                ) : (
                                                                                                                                    <span className="fess-paid-pending">
                                                                                                                                        {" "}
                                                                                                                                        {commonService.LabelDisplayText("paid")}
                                                                                                                                    </span>
                                                                                                                                )}
                                                                                                                            </p>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            )
                                                                                                    )
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}</>
                                                                                }

                                                                                {
                                                                                    commonService.getLoginRoleData("Transport", "IsView") &&
                                                                                    <>{!transportationDetails.IsData ? (
                                                                                        <div className="student-transportation">
                                                                                            <div className="inner-header-for-section mt-4 mb-3">
                                                                                                <h2 className="m-0">{commonService.LabelDisplayText("Transport")}</h2>
                                                                                            </div>
                                                                                            <div className="row">
                                                                                                {" "}
                                                                                                <div className="col-6">
                                                                                                    <div className="student-term-fees">
                                                                                                        <_Skeleton
                                                                                                            type={_SkeletonType.Line}
                                                                                                            count={1}
                                                                                                            lineheight={120}
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="col-6">
                                                                                                    <div className="student-term-fees">
                                                                                                        <_Skeleton
                                                                                                            type={_SkeletonType.Line}
                                                                                                            count={1}
                                                                                                            lineheight={100}
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : (
                                                                                        transportationDetails.data.length !== 0 && (
                                                                                            <div className="student-transportation">
                                                                                                <div className="inner-header-for-section mt-4 mb-3">
                                                                                                    <h2 className="m-0">{commonService.LabelDisplayText("Transport")}</h2>
                                                                                                </div>
                                                                                                <div className="transportation-detail">
                                                                                                    <div className="row">
                                                                                                        <div className="col-6 p-2 pl-3">
                                                                                                            <h6 className="m-0 mb-3 d-flex align-items-center">
                                                                                                                <b>{commonService.LabelDisplayText("PickupDetail")}</b>
                                                                                                            </h6>
                                                                                                            <div className="col-12 p-0">
                                                                                                                <div
                                                                                                                    className="col-12 p-0  pickup-map"
                                                                                                                    style={{ height: "200px" }}
                                                                                                                >
                                                                                                                    <PickupRouteMap
                                                                                                                        google={props.google}
                                                                                                                        transportationData={transportationDetails.data?.[0]}
                                                                                                                        pickupMarker={PickupMarker}
                                                                                                                        initialCenter={PickupMapinitialCenter}
                                                                                                                        windowWidth={windowWidth}
                                                                                                                    />
                                                                                                                </div>
                                                                                                                <div className="col-12 student-detail-bus mt-2">
                                                                                                                    <div className="student-bus-detail">
                                                                                                                        <div className="bus-name-number">
                                                                                                                            <p className="bus-name mr-2">{commonService.LabelDisplayText("Point")}</p>
                                                                                                                            <p className="bus-number-plate ">
                                                                                                                                {transportationDetails.data[0].PickupPointName}
                                                                                                                            </p>
                                                                                                                        </div>
                                                                                                                        <div className="bus-name-number">
                                                                                                                            <p className="bus-name mr-2">{commonService.LabelDisplayText("BusNum")}</p>
                                                                                                                            <p className="bus-number-plate">
                                                                                                                                {transportationDetails.data[0].RTORegNoForPickup || transportationDetails.data[0].RTORegNoForDrop}
                                                                                                                            </p>
                                                                                                                        </div>
                                                                                                                        <div className="bus-timing">
                                                                                                                            <div className="in-time">
                                                                                                                                <i className="icon-fi-rr-time-forward-1 mr-2"> </i>
                                                                                                                                <p className="morning-time">
                                                                                                                                    {
                                                                                                                                        transportationDetails.data[0]
                                                                                                                                            .BusRouteForPickupStartTime
                                                                                                                                    }
                                                                                                                                </p>
                                                                                                                            </div>
                                                                                                                            <div className="in-time ml-3">
                                                                                                                                <i className="icon-fi-rr-time-forward-1 mr-2"> </i>
                                                                                                                                <p className="morning-time ">
                                                                                                                                    {
                                                                                                                                        transportationDetails.data[0]
                                                                                                                                            .BusRouteForPickupEndTime
                                                                                                                                    }
                                                                                                                                </p>
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                        <div className="bus-driver-name">
                                                                                                                            <p className="bus-driver">
                                                                                                                                {commonService.LabelDisplayText("BusDriver")}{" "}
                                                                                                                                <span>
                                                                                                                                    {
                                                                                                                                        transportationDetails.data[0]
                                                                                                                                            .DriverNameForPickup
                                                                                                                                    }{" "}
                                                                                                                                    (
                                                                                                                                    {
                                                                                                                                        transportationDetails.data[0]
                                                                                                                                            .DriverMobileNoForPickup
                                                                                                                                    }
                                                                                                                                    )
                                                                                                                                </span>
                                                                                                                            </p>
                                                                                                                            <p className="bus-driver">
                                                                                                                                {commonService.LabelDisplayText("BusHelper")}{" "}
                                                                                                                                <span>
                                                                                                                                    {
                                                                                                                                        transportationDetails.data[0]
                                                                                                                                            .HelperNameForPickup
                                                                                                                                    }{" "}
                                                                                                                                    (
                                                                                                                                    {
                                                                                                                                        transportationDetails.data[0]
                                                                                                                                            .HelperMobileNoForPickup
                                                                                                                                    }
                                                                                                                                    )
                                                                                                                                </span>
                                                                                                                            </p>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div className="col-6 p-2 pr-3">
                                                                                                            <h6 className="m-0 mb-3">
                                                                                                                <b>{commonService.LabelDisplayText("DropDetail")}</b>
                                                                                                            </h6>
                                                                                                            <div className="col-12 p-0 ">
                                                                                                                <div
                                                                                                                    className="col-12 p-0 Drop-map"
                                                                                                                    style={{ height: "200px" }}
                                                                                                                >
                                                                                                                    <DropRouteMap
                                                                                                                        google={props.google}
                                                                                                                        transportationData={transportationDetails.data?.[0]}
                                                                                                                        dropMarker={DropMarker}
                                                                                                                        initialCenter={DropMapinitialCenter}
                                                                                                                        windowWidth={windowWidth}
                                                                                                                    />
                                                                                                                </div>
                                                                                                            </div>
                                                                                                            <div className="col-12 student-detail-bus  mt-2">
                                                                                                                <div className="student-bus-detail">
                                                                                                                    <div className="bus-name-number">
                                                                                                                        <p className="bus-name mr-2">{commonService.LabelDisplayText("Point")}</p>
                                                                                                                        <p className="bus-number-plate">
                                                                                                                            {transportationDetails.data[0].DropPointName}
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                    <div className="bus-name-number">
                                                                                                                        <p className="bus-name mr-2">{commonService.LabelDisplayText("BusNum")}</p>
                                                                                                                        <p className="bus-number-plate">
                                                                                                                            {transportationDetails.data[0].RTORegNoForDrop || transportationDetails.data[0].RTORegNoForPickup}
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                    <div className="bus-timing">
                                                                                                                        <div className="in-time">
                                                                                                                            <i className="icon-fi-rr-time-forward-1 mr-2"> </i>
                                                                                                                            <p className="morning-time ">
                                                                                                                                {
                                                                                                                                    transportationDetails.data[0]
                                                                                                                                        .BusRouteForDropStartTime
                                                                                                                                }
                                                                                                                            </p>
                                                                                                                        </div>
                                                                                                                        <div className="in-time ml-3">
                                                                                                                            <i className="icon-fi-rr-time-forward-1 mr-2"> </i>
                                                                                                                            <p className="morning-time ">
                                                                                                                                {
                                                                                                                                    transportationDetails.data[0]
                                                                                                                                        .BusRouteForDropEndTime
                                                                                                                                }
                                                                                                                            </p>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                    <div className="bus-driver-name">
                                                                                                                        <p className="bus-driver">
                                                                                                                            {commonService.LabelDisplayText("BusDriver")}{" "}
                                                                                                                            <span>
                                                                                                                                {
                                                                                                                                    transportationDetails.data[0]
                                                                                                                                        .DriverNameForDrop
                                                                                                                                }{" "}
                                                                                                                                (
                                                                                                                                {
                                                                                                                                    transportationDetails.data[0]
                                                                                                                                        .DriverMobileNoForDrop
                                                                                                                                }
                                                                                                                                )
                                                                                                                            </span>
                                                                                                                        </p>
                                                                                                                        <p className="bus-driver">
                                                                                                                            {commonService.LabelDisplayText("BusHelper")}{" "}
                                                                                                                            <span>
                                                                                                                                {
                                                                                                                                    transportationDetails.data[0]
                                                                                                                                        .HelperNameForDrop
                                                                                                                                }{" "}
                                                                                                                                (
                                                                                                                                {
                                                                                                                                    transportationDetails.data[0]
                                                                                                                                        .HelperMobileNoForDrop
                                                                                                                                }
                                                                                                                                )
                                                                                                                            </span>
                                                                                                                        </p>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        )
                                                                                    )}</>
                                                                                }

                                                                            </>


                    }
                </div>
            </div>
        </>
    );
}

export default GoogleApiWrapper({
    apiKey: "AIzaSyC5jLWPa_hRkT8ryV11Tmi_Zpd05hG3S2A",
})(Profile);

//export default Profile;
